<script>
import Breadcrumb from '@/components/global/breadcrumb.vue';
import axios from "axios";
import BulkSms from "@/components/reservation/BulkSms.vue";
import Swal from "sweetalert2";
import Dropdown from 'primevue/dropdown';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: "lessons",
  inject: {
    courseCtx: { default: null },
  },
  data() {
    return {
      gradeSlug: this.$route.params.gradeSlug,
      courseId: this.$route.params.courseId ?? null,
      dragIndex: null,
      lessons: [],
      chapter: null,
      chapters: [],
    };
  },
  components: {
    BulkSms,
    Breadcrumb,
    Dropdown
  },
  computed: {
    isCourseScope() { return !!this.courseId; },
    basePath() {
      return this.isCourseScope
          ? `/courses/${this.courseId}/lessons`
          : `/lesson/${this.gradeSlug}`;
    },
    contentsBasePath() {
      return this.isCourseScope
          ? `/courses/${this.courseId}/contents`
          : `/${this.gradeSlug}/content`;
    },
    examsBasePath() {
      return this.isCourseScope
          ? `/courses/${this.courseId}/exams`
          : `/${this.gradeSlug}/exam`;
    },
    breadcrumbs() {
      if (this.isCourseScope) {
        const base = this.courseCtx?.breadcrumbBase ?? [];
        return [...base, { name: 'الدروس', path: this.basePath }];
      }
      return [
        { name: 'الرئيسية', path: '/' },
        { name: 'الدروس', path: this.basePath },
      ];
    },
    gradeStore() { return useGradeStore(); },
    resolvedGrade() {
      const slug = this.gradeSlug;
      return this.gradeStore.activeGrades.find(g => g.slug === slug)
          || this.gradeStore.allGrades.find(g => g.slug === slug)
          || null;
    },
    gradeId() {
      return this.resolvedGrade?.id ?? null;
    },
  },
  methods: {
    fetchLessons() {
      const params = this.isCourseScope
          ? { type: 3, course_id: this.courseId }
          : { type: 3, academic_year_id: this.gradeId };
      if (this.chapter) {
        params.parent_id = this.chapter.id;
      }

      axios
          .get('/lessons', { params })
          .then((response) => {
            this.lessons = response.data.data;
          })
          .catch(() => {
            this.lessons = [];
          });
    },
    deleteLesson(lesson_id) {
      Swal.fire({
        title: "هل تريد الحذف ؟",
        text: "سيتم الحذف نهائيا",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "رجوع",
        confirmButtonText: "احذف",
      }).then((result) => {
        if (result.isConfirmed) {
          axios
              .delete(`lessons/${lesson_id}`)
              .then(({ data }) => {
                if (data.status == true) {
                  Swal.fire("تم الحذف بنجاح", "", "success");
                  this.lessons = this.lessons.filter(r => r.id !== lesson_id);
                } else {
                  Swal.fire("", data.message, "error");
                }
              });
        }
      });
    },

    async toggleHide(lesson) {
      const newHide = lesson.hide ? 0 : 1;
      try {
        await axios.post('lessons/hide', {
          lesson_id: lesson.id,
          hide: newHide
        });
        lesson.hide = newHide;
      } catch (e) {
        // handle error (optional)
      }
    },

    fetchChapters() {
      const params = this.isCourseScope
          ? { type: 2, course_id: this.courseId }
          : { type: 2, academic_year_id: this.gradeId };
      axios
          .get('/lessons', { params })
          .then((response) => {
            this.chapters = response.data.data;
          })
          .catch(() => {});
    },

    onDragStart(index) {
      this.dragIndex = index;
    },
    async onDrop(targetIndex) {
      if (this.dragIndex === null || this.dragIndex === targetIndex) return;
      const moved = this.lessons.splice(this.dragIndex, 1)[0];
      this.lessons.splice(targetIndex, 0, moved);
      await axios.post('lessons/reorder', {
        lesson_id: moved.id,
        order: targetIndex + 1
      });
      this.dragIndex = null;
    },

    customLabelChapter(option) {
      return option ? (option.description ? `${option.title} (${option.description})` : option.title) : '';
    },
  },
  async mounted() {
    if (!this.isCourseScope) {
      if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
      if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
    }
    await this.fetchChapters();
    const chapterId = this.$route.query.chapter_id;
    if (chapterId) {
      this.chapter = this.chapters.find(c => String(c.id) === String(chapterId)) || null;
    }
    this.fetchLessons();
  }
}
</script>

<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="container-fluid my-3">
    <div class="filter my-3">
      <div class="filter-head">
        <div class="row d-flex align-items-center gap-4 w-75">
          <h4 class="mb-0 col-lg-2 col-12">الدروس</h4>
          <div class="row d-flex gap-lg-0 gap-2 col-lg-10 col-12 mb-lg-0 mb-2">
            <div class="col-lg-4 col-md-6 col-12">
              <div class="d-flex flex-column gap-2">
                <Dropdown v-model="chapter" :options="chapters" showClear :optionLabel="customLabelChapter" placeholder="اختر الباب" class="w-full md:w-14rem" @update:model-value="fetchLessons" />
              </div>
            </div>
          </div>
        </div>
        <div class="buttons">
          <router-link :to="`${basePath}/add`" class="btn PrimaryButton">إضافة درس</router-link>
        </div>
      </div>
    </div>
    <div class="row reservation-table">
      <div class="col-12">
        <div class="table-responsive">
          <table class="table table-striped">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">العنوان</th>
                <th scope="col">الوصف</th>
                <th scope="col">الباب</th>
                <th scope="col">الحالة</th>
                <th scope="col">التحكم</th>
              </tr>
            </thead>
            <tbody>
              <tr
                  v-for="(lesson, index) in lessons"
                  :key="lesson.id"
                  draggable="true"
                  @dragstart="onDragStart(index)"
                  @dragover.prevent
                  @drop="onDrop(index)"
                  style="cursor: move;"
                >
                <td data-label="ID">{{ index + 1 }}</td>
                <td data-label="الاسم">{{ lesson.title }}</td>
                <td data-label="الوصف">{{ lesson.description }}</td>
                <td data-label="الباب">{{ lesson.parent ? lesson.parent.title : '---' }}</td>
                <td data-label="الحالة">{{ lesson.hide ? "مخفي" : "ظاهر" }}</td>
                <td data-label="التحكم">
                  <router-link class="btn btn-secondary text-white mx-1"
                               :tooltip="'إضافة محتوى'"
                               flow="up"
                               :to="`${contentsBasePath}/add?lesson_id=${lesson.id}`">
                    <i class="pi pi-cloud-upload"></i>
                  </router-link>
                  <router-link class="btn btn-secondary text-white mx-1"
                               :tooltip="'إضافة امتحان'"
                               flow="up"
                               :to="`${examsBasePath}/add?lesson_id=${lesson.id}`">
                    <i class="pi pi-file-edit"></i>
                  </router-link>
                  <router-link class="btn btn-primary text-white mx-1"
                               :tooltip="'تعديل'"
                               flow="up"
                               :to="`${basePath}/edit/${lesson.id}`">
                    <i class="pi pi-pencil"></i>
                  </router-link>
                  <button
                      :class="lesson.hide ? 'btn btn-success' : 'btn btn-warning'"
                      @click="toggleHide(lesson)"
                      :tooltip="lesson.hide ? 'إظهار' : 'إخفاء'"
                      flow="up"
                  >
                    <i :class="lesson.hide ? 'pi pi-eye' : 'pi pi-eye-slash'"></i>
                  </button>
                  <button class="btn btn-danger text-white mx-1"
                          type="button"
                          :tooltip="`حذف`"
                          flow="up"
                          @click="deleteLesson(lesson.id)">
                    <i class="pi pi-trash"></i>
                  </button>
                </td>
              </tr>
              <tr v-if="lessons.length === 0">
                <td colspan="6" class="text-center">لا يوجد بيانات</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
